import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, MapPin, Users, Layers, Tv, Clock, Smartphone, CheckCircle2 } from 'lucide-react';
import CountUp from '../../components/CountUp';

const targetingTypes = [
  {
    icon: <MapPin className="w-6 h-6" />,
    label: 'Geo & Hyperlocal',
    features: ['DMA, city, zip, or radius targeting', 'Down to 1-mile radius from any address', 'Drive-time zone mapping', 'Perfect for brick-and-mortar SMBs'],
    outcome: 'A dentist in Scottsdale reaches households within 3 miles — not the whole metro.'
  },
  {
    icon: <Users className="w-6 h-6" />,
    label: 'Audience',
    features: ['Household income, age, life stage', 'Purchase intent signals', 'First-party data onboarding', 'Lookalike audience modeling'],
    outcome: 'A home services client reaches homeowners actively researching HVAC — not renters.'
  },
  {
    icon: <Layers className="w-6 h-6" />,
    label: 'Contextual',
    features: ['Genre & content category alignment', 'Brand safety controls by vertical', 'Sports, news, lifestyle, kids — per client', 'Daypart scheduling'],
    outcome: 'An auto dealer runs during sports — where in-market buyers are watching.'
  }
];

const secondScreenFeatures = [
  'Household graph matching — CTV to mobile',
  'Cross-device sequential messaging',
  'Social audience sync (Meta, Google)',
  'Configurable retargeting windows — 1 to 30 days'
];

const sequence = [
  { icon: <Tv className="w-5 h-5" />, text: 'CTV ad seen on streaming TV' },
  { icon: <Clock className="w-5 h-5" />, text: 'Household matched via device graph' },
  { icon: <Smartphone className="w-5 h-5" />, text: 'Follow-up ad on phone/tablet — within hours' },
  { icon: <CheckCircle2 className="w-5 h-5" />, text: 'Conversion tracked back to original CTV exposure' }
];

const CastTargetingPage = () => {
  return (
    <div className="min-h-screen" data-testid="cast-targeting-page">
      <Helmet><title>MarqiCast — Targeting & Retargeting</title></Helmet>

      {/* Hero */}
      <section className="py-24 md:py-32 text-center">
        <div className="container max-w-4xl">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="cast-targeting-hero-title">
            Reach Exactly the Right Household.
            <br /><span className="text-[var(--accent-primary)]">Not Just the Right Zip Code.</span>
          </h1>
          <p className="body-lg mb-12 max-w-3xl mx-auto">
            Most digital ads reach a general area. MarqiCast targeting goes down to a 1-mile radius —
            matching your client's ideal customers by location, income, purchase intent, household
            composition, and what they're watching. Then it follows them to their second screen.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="cast-targeting-cta-primary">
              See Targeting in Action
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary">Book a Strategy Session</Link>
          </div>
        </div>
      </section>

      {/* 3 targeting types */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Three Layers of Precision. One Campaign.</h2>
            <p className="body-lg">Stack all three targeting types in a single campaign — or use them individually depending on your client's objectives.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {targetingTypes.map((t, i) => (
              <div key={t.label} className="feature-card hover-lift animate-fade-in" style={{ animationDelay: `${i * 0.12}s` }}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] mr-4">{t.icon}</div>
                  <h3 className="text-lg font-bold text-[var(--accent-primary)] uppercase tracking-wide">{t.label}</h3>
                </div>
                <ul className="space-y-3 mb-6">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)]">{f}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[var(--text-muted)] pt-4 border-t border-[var(--border-subtle)]">{t.outcome}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Second screen retargeting */}
      <section className="py-24">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="h1 text-[var(--text-primary)] mb-6">
                CTV Starts the Conversation.
                <br /><span className="text-[var(--accent-primary)]">The Second Screen Closes the Deal.</span>
              </h2>
              <p className="body-lg mb-8">
                Someone in your client's target area sees their ad on a streaming TV. Within hours, that
                same household sees a follow-up ad on their phone, tablet, or laptop. This is the most
                powerful advertising sequence available to SMBs today — and your clients can have it
                through MarqiCast.
              </p>
              <ul className="space-y-3 mb-10">
                {secondScreenFeatures.map((f) => (
                  <li key={f} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)]">{f}</span>
                  </li>
                ))}
              </ul>
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <CountUp value="+36%" className="block text-5xl font-bold text-[var(--accent-primary)] mb-2" />
                  <p className="text-sm text-[var(--text-muted)]">Unaided brand recall — Interactive CTV benchmarks 2025</p>
                </div>
                <div>
                  <CountUp value="+13%" className="block text-5xl font-bold text-[var(--accent-primary)] mb-2" />
                  <p className="text-sm text-[var(--text-muted)]">Foot traffic lift from CTV + second screen sequences</p>
                </div>
              </div>
            </div>

            {/* The sequence */}
            <div className="feature-card bg-[var(--bg-secondary)]">
              <h3 className="text-sm uppercase tracking-wider text-[var(--accent-primary)] font-semibold mb-8">The Sequence</h3>
              <div className="space-y-2">
                {sequence.map((s, i) => (
                  <React.Fragment key={s.text}>
                    <div className="flex items-center space-x-4 p-4 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-subtle)] animate-fade-in" style={{ animationDelay: `${i * 0.15}s` }}>
                      <div className="w-10 h-10 rounded-full bg-[var(--accent-bg)] flex items-center justify-center text-[var(--accent-primary)] flex-shrink-0">{s.icon}</div>
                      <div className="flex items-center">
                        <span className="text-2xl font-bold text-[var(--accent-primary)] mr-3">{i + 1}</span>
                        <span className="text-[var(--text-secondary)] text-sm">{s.text}</span>
                      </div>
                    </div>
                    {i < sequence.length - 1 && (
                      <div className="flex justify-center"><div className="w-px h-5 bg-[var(--accent-primary)] opacity-50"></div></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[var(--bg-secondary)]">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <h2 className="h1 text-[var(--text-primary)] mb-6">Show Your Clients Who They Can Actually Reach.</h2>
            <p className="body-lg mb-12 max-w-2xl mx-auto">In your strategy session we'll build a live audience for one of your existing clients — so you can show them the targeting before spending a dollar.</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect">Book Your Strategy Session<ArrowRight className="ml-2 w-5 h-5" /></Link>
              <Link to="/cast" className="btn-secondary">Back to MarqiCast Overview</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CastTargetingPage;
